<template>
  <div class="fast-nav tw-flex tw-h-full tw-gap-x-8">
    <template v-for="(item, index) in menus" :key="`menu-${item._id}`">
      <!-- Regular menu items without submenu -->
      <div v-if="!item.subMenus" class="tw-h-full">
        <QBtn
          flat
          square
          :ripple="false"
          :class="[
            'fast-nav-dropdown-menu-item tw-h-full tw-px-0',
            {
              'menu-active': isActive(item),
              'hover-active': activeDropdown === index,
            },
          ]"
          no-caps
          @mouseenter="handleMouseEnter(index)"
          @mouseleave="handleMouseLeave()"
        >
          <div class="fast-nav-dropdown-menu-border" />
          <a
            v-if="item.title && item.code === MENU_CODE.EXTERNAL_LINK"
            :href="item.externalLink || '#'"
            :title="item.title"
            target="_blank"
            rel="noopener noreferrer"
            :class="['tw-h-full tw-flex tw-items-center', props.menuTextClass]"
            @click="eventTopMenu(item.title, item.code)"
          >
            {{ item.title }}
          </a>
          <NuxtLink
            v-else-if="item.title"
            :to="redirectPath(item.componentName, item.slug, item.code)"
            :title="item.title"
            :class="['tw-h-full tw-flex tw-items-center', props.menuTextClass]"
            @click="eventTopMenu(item.title, item.code)"
          >
            {{ item.title }}
          </NuxtLink>
        </QBtn>
      </div>

      <!-- Menu items with submenu -->
      <div
        v-else-if="item.subMenus && item.subMenus.length > 0"
        class="tw-relative tw-h-full"
        @mouseenter="onSubmenuEnter(index)"
        @mouseleave="handleMouseLeave()"
      >
        <QBtn
          flat
          square
          :ripple="false"
          :class="[
            'fast-nav-dropdown-menu-item tw-h-full tw-px-0',
            {
              'menu-active': isActive(item),
              'hover-active': activeDropdown === index,
            },
          ]"
          no-caps
          @click="handleMenuClick(item)"
        >
          <div class="fast-nav-dropdown-menu-border" />
          <a
            :class="['tw-h-full tw-flex tw-items-center tw-gap-3', props.menuTextClass]"
            :title="item.title"
          >
            {{ item.title }}
            <QIcon name="expand_more" size="sm" />
          </a>
        </QBtn>

        <!-- Submenu dropdown -->
        <div
          v-show="activeDropdown === index"
          :ref="
            (el) => {
              if (el) submenuEls[index] = el as HTMLElement;
            }
          "
          :class="[
            'fast-nav-submenu-container tw-absolute tw-top-full tw-bg-white tw-shadow-lg tw-pt-6 tw-px-6 tw-pb-0 tw-z-10 tw-max-w-[calc(100vw-2rem)]',
            { 'tw-rounded-b-md': props.submenuRounded },
          ]"
        >
          <QList
            :class="[
              'tw-grid tw-gap-6 tw-mb-6',
              {
                'tw-grid-flow-col': item.subMenus.length > 10,
                'tw-grid-cols-1 tw-min-w-56': item.subMenus.length <= 10,
                'tw-grid-cols-2 tw-min-w-450':
                  item.subMenus.length > 10 && item.subMenus.length <= 20,
                'tw-grid-cols-3 tw-min-w-600': item.subMenus.length > 20,
              },
            ]"
            :style="item.subMenus.length > 10 ? { gridTemplateRows: `repeat(${Math.ceil(item.subMenus.length / (item.subMenus.length > 20 ? 3 : 2))}, auto)` } : {}"
          >
            <QItem
              v-for="(subMenu, subIndex) in item.subMenus"
              :key="`subMenu-${subMenu._id}`"
              v-close-popup
              :to="
                getPath({
                  componentName: subMenu.subMenuOf
                    ? subMenu.componentName
                    : item.componentName,
                  categorySlug:
                    item.subMenus && item.subMenus.length > 0
                      ? subMenu.slug
                      : item.slug,
                  slug:
                    item.code === MENU_CODE.GALLERY_REVIEW ? subMenu.slug : '',
                  subMenuOf: subMenu.subMenuOf,
                })
              "
              dense
              clickable
              :class="[
                'fast-nav-submenu-item tw-p-0 tw-min-h-fit',
                {
                  'tw-rounded-b-md': subIndex === item.subMenus.length - 1,
                },
              ]"
              manual-focus
              :focused="false"
              :title="subMenu.title"
              @click="eventTopMenu(subMenu.title, 'submenu')"
            >
              <QItemSection>
                <QItemLabel
                  class="tw-text-mb-body-small lg:tw-text-dt-body tw-bg-white tw-text-black"
                >
                  <span class="tw-relative tw-inline-block">
                    {{ subMenu.title }}
                    <div
                      v-if="!props.hideSubmenuBorder"
                      class="fast-nav-submenu-border"
                    />
                  </span>
                </QItemLabel>
              </QItemSection>
            </QItem>
          </QList>
          <div
            v-if="props.showSubmenuBottomBar"
            class="fast-nav-submenu-bottom-bar tw-bg-primary -tw-mx-6 tw-h-1 tw-rounded-b-lg"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { MENU_CODE } from '@/constants/menu';

const props = defineProps({
  menuTextClass: {
    type: String,
    default: 'tw-text-dt-title-4',
  },
  showSubmenuBottomBar: {
    type: Boolean,
    default: false,
  },
  submenuRounded: {
    type: Boolean,
    default: false,
  },
  hideSubmenuBorder: {
    type: Boolean,
    default: false,
  },
});

const {
  menus,
  activeDropdown,
  handleMouseEnter,
  handleMouseLeave,
  handleMenuClick,
  eventTopMenu,
  isActive,
  redirectPath,
  getPath,
} = useHeaderNavigation();

const submenuEls = ref<Record<number, HTMLElement>>({});

function onSubmenuEnter(index: number) {
  handleMouseEnter(index);
  nextTick(() => {
    adjustSubmenuPosition(index);
  });
}

function adjustSubmenuPosition(index: number) {
  const el = submenuEls.value[index];
  if (!el) return;

  // Reset ก่อนวัด
  el.style.transform = '';
  requestAnimationFrame(() => {
    const rect = el.getBoundingClientRect();
    const margin = 16;

    if (rect.right > window.innerWidth - margin) {
      const overflow = rect.right - window.innerWidth + margin;
      el.style.transform = `translateX(-${overflow}px)`;
    } else if (rect.left < margin) {
      const overflow = margin - rect.left;
      el.style.transform = `translateX(${overflow}px)`;
    }
  });
}
</script>

<style lang="scss">
@use '~/assets/styles/token' as *;

.fast-nav-submenu-animation {
  transition: opacity 0.2s ease-in-out;
}

.fast-nav-dropdown-menu-item {
  @apply tw-relative;
}

.fast-nav-dropdown-menu-border {
  @apply tw-absolute tw-left-0 tw-w-0 tw-h-[3px];
  bottom: 0px !important;
  transition: width 0.2s ease-in-out;
}

.fast-nav-dropdown-menu-item:hover .fast-nav-dropdown-menu-border,
.fast-nav-dropdown-menu-item.menu-active .fast-nav-dropdown-menu-border,
.fast-nav-dropdown-menu-item.hover-active .fast-nav-dropdown-menu-border {
  @apply tw-w-full;
}

.fast-nav-submenu-border {
  @apply tw-absolute -tw-bottom-1 tw-left-0 tw-w-0 tw-h-[1px];
  transition: width 0.2s ease-in-out;
}

.fast-nav-submenu-item {
  background: transparent !important;
  color: var(--theme-color-black) !important;
}

.fast-nav-submenu-item .q-item__label {
  color: inherit !important;
}

.fast-nav-submenu-item:hover {
  -webkit-text-fill-color: $primary;
  color: $primary !important;

  .fast-nav-submenu-border {
    @apply tw-w-full;
  }
}

.fast-nav-dropdown-menu-item.q-btn::before,
.fast-nav-dropdown-menu-item.q-btn::after {
  display: none !important;
}

.fast-nav-dropdown-menu-item.q-btn {
  outline: none !important;
  box-shadow: none !important;
}

.fast-nav-dropdown-menu-item.q-btn .q-focus-helper {
  display: none !important;
}

.fast-nav a .q-icon {
  color: inherit !important;
}
</style>
